import { useEffect, useState, useMemo } from "react";
import { useSupplier, useProjectSuppliers, useCart } from "../../../hooks/query";
import { useSelectedSupplier } from "../../WorkEstimation/components/hooks";
import { useProject } from "../../../hooks";
import type { ISupplier } from "../../../interfaces";
import type { PurchaseStrategy, SavedSupplierInfo } from "../types";

interface UseSupplierComparisonLogicProps {
    selectedValues: any;
    setIsNextStageEligible: any;
}

export function useSupplierComparisonLogic({
    selectedValues,
    setIsNextStageEligible,
}: UseSupplierComparisonLogicProps) {
    const [openSupplierComparison, setOpenSupplierComparison] = useState(false);
    const [comparisonResults, setComparisonResults] = useState<any[]>([]);
    const [purchaseStrategy, setPurchaseStrategy] = useState<PurchaseStrategy>("single");
    const [selectedSuppliers, setSelectedSuppliers] = useState<ISupplier[]>([]);
    const [isComparing, setIsComparing] = useState(false);
    const [supplierSearchQuery, setSupplierSearchQuery] = useState("");
    const [savedProjectSupplierIds, setSavedProjectSupplierIds] = useState<string[]>([]);
    const [savedSuppliersInfo, setSavedSuppliersInfo] = useState<SavedSupplierInfo[]>([]);
    const [itemSupplierSelections, setItemSupplierSelections] = useState<Record<string, string>>({});
    const [isChangingSelection, setIsChangingSelection] = useState(false);
    const [originalItemSelections, setOriginalItemSelections] = useState<Record<string, string>>({});
    const [originalSelectedSupplierId, setOriginalSelectedSupplierId] = useState<string | null>(null);

    const { selectedSupplier, setSelectedSupplier } = useSelectedSupplier();
    const { project } = useProject();
    const { useSupplierComparison, useList } = useSupplier();
    const {
        useCreate: useCreateProjectSupplier,
        useList: useListProjectSuppliers,
        useDelete: useDeleteProjectSupplier,
        useListWithInventory: useListProjectSuppliersWithInventory,
    } = useProjectSuppliers();
    const { useUpdate: useUpdateCart, useListByProjectId: useListCartByProjectId } = useCart();
    const supplierComparisonSvc = useSupplierComparison();
    const { data: suppliers = [], isLoading: isLoadingSuppliers } = useList();
    const createProjectSupplierSvc = useCreateProjectSupplier();
    const listProjectSuppliersSvc = useListProjectSuppliers();
    const listProjectSuppliersWithInventorySvc = useListProjectSuppliersWithInventory();
    const deleteProjectSupplierSvc = useDeleteProjectSupplier();
    const updateCartSvc = useUpdateCart();
    const listCartByProjectIdSvc = useListCartByProjectId();

    // Filter active suppliers only
    const activeSuppliers = useMemo(() => {
        return suppliers.filter((s: ISupplier) => s.Status === "Active");
    }, [suppliers]);

    // Helper: Find supplier by ID
    const findSupplierById = (supplierId: string): ISupplier | undefined => {
        return suppliers.find((s: ISupplier) => s.Id === supplierId);
    };

    // Helper: Build supplier ID to name map from project suppliers
    const buildSupplierIdToNameMap = (suppliersWithInventory: any[]): Record<string, string> => {
        const map: Record<string, string> = {};
        suppliersWithInventory.forEach((ps: any) => {
            map[ps.SupplierId] = ps.SupplierName;
        });
        return map;
    };

    // Helper: Extract item supplier selections from inventory
    const extractItemSelections = (
        suppliersWithInventory: any[],
        supplierIdToNameMap: Record<string, string>
    ): Record<string, string> => {
        const selections: Record<string, string> = {};
        suppliersWithInventory.forEach((ps: any) => {
            if (!ps.Inventory || ps.Inventory.length === 0) return;
            ps.Inventory.forEach((inv: any) => {
                if (inv.ProductName && inv.CartSupplierId) {
                    const selectedSupplierName = supplierIdToNameMap[inv.CartSupplierId];
                    if (selectedSupplierName) {
                        selections[inv.ProductName] = selectedSupplierName;
                    }
                }
            });
        });
        return selections;
    };

    // Toggle supplier selection
    const toggleSupplierSelection = (supplier: ISupplier) => {
        setSelectedSuppliers((prev) => {
            const isSelected = prev.some((s) => s.Id === supplier.Id);
            if (isSelected) {
                return prev.filter((s) => s.Id !== supplier.Id);
            }
            return [...prev, supplier];
        });
    };

    // Helper: Build supplier product map from comparison results
    const buildSupplierProductMap = (
        results: any[]
    ): {
        allProducts: Set<string>;
        supplierProductMap: Record<string, Set<string>>;
    } => {
        const allProducts = new Set<string>();
        const supplierProductMap: Record<string, Set<string>> = {};

        results.forEach((supplier: any) => {
            if (!supplier?.SupplierInventory) return;
            const supplierName = supplier.SupplierName;
            supplierProductMap[supplierName] = new Set();

            supplier.SupplierInventory.forEach((product: any) => {
                if (product?.ProductName && product.UnitPrice > 0) {
                    allProducts.add(product.ProductName);
                    supplierProductMap[supplierName].add(product.ProductName);
                }
            });
        });

        return { allProducts, supplierProductMap };
    };

    // Helper: Determine purchase strategy from supplier product map
    const determinePurchaseStrategy = (
        allProducts: Set<string>,
        supplierProductMap: Record<string, Set<string>>
    ): { strategy: PurchaseStrategy; singleSupplierName: string | null } => {
        for (const [supplierName, products] of Object.entries(supplierProductMap)) {
            if (products.size === allProducts.size) {
                return { strategy: "single", singleSupplierName: supplierName };
            }
        }
        return { strategy: "multiple", singleSupplierName: null };
    };

    const handleOnCompare = async () => {
        if (selectedSuppliers.length === 0 || !project?.id) {
            return;
        }

        setIsComparing(true);
        try {
            const supplierNames = selectedSuppliers.map((s) => s.Name);
            const params = {
                SupplierNames: supplierNames,
                ProjectId: project.id,
            };
            const results = await supplierComparisonSvc.mutateAsync(params);
            setComparisonResults(results);

            const { allProducts, supplierProductMap } = buildSupplierProductMap(results);
            const { strategy, singleSupplierName } = determinePurchaseStrategy(allProducts, supplierProductMap);

            setPurchaseStrategy(strategy);
            if (strategy === "single" && singleSupplierName) {
                setSelectedSupplier({ name: singleSupplierName, id: singleSupplierName });
            } else {
                setSelectedSupplier(null);
                setItemSupplierSelections({});
            }

            setOpenSupplierComparison(true);
        } catch (error) {
            console.error("Error in materials supplier comparison:", error);
        } finally {
            setIsComparing(false);
        }
    };

    // Helper: Get suppliers to pre-select from saved info
    const getSuppliersToPreSelect = (): ISupplier[] => {
        const suppliersToPreSelect: ISupplier[] = [];

        if (savedSuppliersInfo.length > 0) {
            savedSuppliersInfo.forEach((savedInfo) => {
                const supplier = findSupplierById(savedInfo.id);
                if (supplier) {
                    suppliersToPreSelect.push(supplier);
                }
            });
            return suppliersToPreSelect;
        }

        if (selectedSupplier) {
            const supplier = findSupplierById(selectedSupplier.id);
            if (supplier) {
                suppliersToPreSelect.push(supplier);
            }
        }

        return suppliersToPreSelect;
    };

    // Helper: Restore item selections from saved inventory
    const restoreItemSelections = async (): Promise<Record<string, string>> => {
        if (!project?.id) {
            return {};
        }

        try {
            const suppliersWithInventory = await listProjectSuppliersWithInventorySvc.mutateAsync({
                ProjectId: project.id,
            });

            if (!suppliersWithInventory || suppliersWithInventory.length <= 1) {
                return {};
            }

            const supplierIdToNameMap = buildSupplierIdToNameMap(suppliersWithInventory);
            return extractItemSelections(suppliersWithInventory, supplierIdToNameMap);
        } catch (error) {
            console.error("Error loading saved selections for restore:", error);
            return {};
        }
    };

    const handleClearSelection = async () => {
        const suppliersToPreSelect = getSuppliersToPreSelect();
        const restoredItemSelections = await restoreItemSelections();

        // Don't delete from database here - only reset UI state
        // Deletion will happen in handleSupplierConfirmation when user confirms new selection
        setIsChangingSelection(true);
        setSelectedSupplier(null);
        setSelectedSuppliers(suppliersToPreSelect);
        setSupplierSearchQuery("");
        setComparisonResults([]);
        setItemSupplierSelections(restoredItemSelections);
        // Keep savedSuppliersInfo and savedProjectSupplierIds so we can restore state if user cancels
    };

    const handleItemSupplierSelect = (productName: string, supplierName: string) => {
        setItemSupplierSelections((prev) => {
            const newSelections = { ...prev };
            if (newSelections[productName] === supplierName) {
                delete newSelections[productName];
            } else {
                newSelections[productName] = supplierName;
            }
            return newSelections;
        });
    };

    // Helper: Process suppliers with inventory and set state
    const processSuppliersWithInventory = (suppliersWithInventory: any[]) => {
        setSavedProjectSupplierIds(suppliersWithInventory.map((ps: any) => ps.Id));
        setSavedSuppliersInfo(
            suppliersWithInventory.map((ps: any) => ({
                id: ps.SupplierId,
                name: ps.SupplierName,
            }))
        );

        if (suppliersWithInventory.length > 1) {
            const supplierIdToNameMap = buildSupplierIdToNameMap(suppliersWithInventory);
            const selections = extractItemSelections(suppliersWithInventory, supplierIdToNameMap);
            setPurchaseStrategy("multiple");
            setItemSupplierSelections(selections);
            setOriginalItemSelections(selections); // Store original for comparison
            setSelectedSupplier(null);
            setOriginalSelectedSupplierId(null);
            return;
        }

        const firstSupplier = suppliersWithInventory[0];
        const supplier = findSupplierById(firstSupplier.SupplierId);
        if (supplier) {
            setPurchaseStrategy("single");
            setSelectedSupplier({
                id: supplier.Id,
                name: supplier.Name,
            });
            setOriginalSelectedSupplierId(supplier.Id); // Store original for comparison
            setItemSupplierSelections({});
            setOriginalItemSelections({});
        }
    };

    // Helper: Process suppliers without inventory (fallback)
    const processSuppliersFallback = (savedSuppliers: any[]) => {
        setSavedProjectSupplierIds(savedSuppliers.map((ps: any) => ps.Id));
        setSavedSuppliersInfo(
            savedSuppliers.map((ps: any) => ({
                id: ps.SupplierId,
                name: ps.SupplierName,
            }))
        );

        if (savedSuppliers.length > 1) {
            setPurchaseStrategy("multiple");
            setSelectedSupplier(null);
            setOriginalSelectedSupplierId(null);
            setOriginalItemSelections({});
            return;
        }

        const firstSupplier = savedSuppliers[0];
        const supplier = findSupplierById(firstSupplier.SupplierId);
        if (supplier) {
            setPurchaseStrategy("single");
            setSelectedSupplier({
                id: supplier.Id,
                name: supplier.Name,
            });
            setOriginalSelectedSupplierId(supplier.Id); // Store original for comparison
            setItemSupplierSelections({});
            setOriginalItemSelections({});
        }
    };

    // Load saved supplier selection on mount
    useEffect(() => {
        const loadSavedSuppliers = async () => {
            if (!project?.id || suppliers.length === 0) return;

            try {
                const suppliersWithInventory = await listProjectSuppliersWithInventorySvc.mutateAsync({
                    ProjectId: project.id,
                });

                if (suppliersWithInventory && suppliersWithInventory.length > 0) {
                    processSuppliersWithInventory(suppliersWithInventory);
                    return;
                }
            } catch (error) {
                console.error("Error loading saved suppliers:", error);
            }

            // Fallback: try without inventory
            try {
                const savedSuppliers = await listProjectSuppliersSvc.mutateAsync({ ProjectId: project.id });
                if (savedSuppliers && savedSuppliers.length > 0) {
                    processSuppliersFallback(savedSuppliers);
                }
            } catch (fallbackError) {
                console.error("Error loading saved suppliers (fallback):", fallbackError);
            }
        };

        loadSavedSuppliers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id, suppliers.length]);

    // Helper: Delete old project suppliers
    const deleteOldProjectSuppliers = async () => {
        if (savedProjectSupplierIds.length === 0) return;
        for (const id of savedProjectSupplierIds) {
            await deleteProjectSupplierSvc.mutateAsync({ id });
        }
        setSavedProjectSupplierIds([]);
    };

    // Helper: Check if single supplier selection has changed
    const hasSingleSupplierChanged = (supplierId: string): boolean => {
        return originalSelectedSupplierId !== supplierId;
    };

    // Helper: Check if multiple supplier selections have changed
    const hasMultipleSupplierSelectionsChanged = (newSelections: Record<string, string>): boolean => {
        const newKeys = Object.keys(newSelections).sort();
        const originalKeys = Object.keys(originalItemSelections).sort();

        if (newKeys.length !== originalKeys.length) return true;

        for (const key of newKeys) {
            if (newSelections[key] !== originalItemSelections[key]) {
                return true;
            }
        }

        return false;
    };

    // Helper: Handle single supplier confirmation
    const handleSingleSupplierConfirmation = async (cartItems: any[]) => {
        const supplier = suppliers.find((s: ISupplier) => s.Name === selectedSupplier?.name);
        if (!supplier) return;

        const hasChanged = hasSingleSupplierChanged(supplier.Id);

        await createProjectSupplierSvc.mutateAsync({
            ProjectId: project!.id,
            SupplierId: supplier.Id,
            SelectionType: "manual",
        });

        // Only update cart items if selection has changed
        if (!hasChanged || !Array.isArray(cartItems)) return;

        for (const cartItem of cartItems) {
            if (cartItem.Id && cartItem.SupplierId !== supplier.Id) {
                await updateCartSvc.mutateAsync({
                    Id: cartItem.Id,
                    SupplierId: supplier.Id,
                });
            }
        }
    };

    // Helper: Handle multiple supplier confirmation
    const handleMultipleSupplierConfirmation = async (cartItems: any[]) => {
        const uniqueSupplierNames = Array.from(new Set(Object.values(itemSupplierSelections)));
        const supplierNameToIdMap: Record<string, string> = {};

        // Create project suppliers and build map
        for (const supplierName of uniqueSupplierNames) {
            const supplier = suppliers.find((s: ISupplier) => s.Name === supplierName);
            if (supplier) {
                supplierNameToIdMap[supplierName] = supplier.Id;
                await createProjectSupplierSvc.mutateAsync({
                    ProjectId: project!.id,
                    SupplierId: supplier.Id,
                    SelectionType: "manual",
                });
            }
        }

        // Check if selections have changed
        const hasChanged = hasMultipleSupplierSelectionsChanged(itemSupplierSelections);

        // Only update cart items if selection has changed
        if (!hasChanged || !Array.isArray(cartItems)) return;

        // Build a map of current cart item supplier assignments for comparison
        const cartItemSupplierMap: Record<string, string> = {};
        cartItems.forEach((item) => {
            if (item.Id && item.ProductName && item.SupplierId) {
                cartItemSupplierMap[item.ProductName] = item.SupplierId;
            }
        });

        // Update only changed cart items
        for (const cartItem of cartItems) {
            if (!cartItem.Id || !cartItem.ProductName) continue;
            const selectedSupplierName = itemSupplierSelections[cartItem.ProductName];
            if (!selectedSupplierName) continue;
            const supplierId = supplierNameToIdMap[selectedSupplierName];
            if (supplierId && cartItemSupplierMap[cartItem.ProductName] !== supplierId) {
                await updateCartSvc.mutateAsync({
                    Id: cartItem.Id,
                    SupplierId: supplierId,
                });
            }
        }
    };

    // Helper: Update context supplier for pricing
    const updateContextSupplier = () => {
        if (purchaseStrategy === "single" && selectedSupplier) {
            const supplier = suppliers.find((s: ISupplier) => s.Name === selectedSupplier.name);
            if (supplier) {
                setSelectedSupplier({
                    id: supplier.Id,
                    name: supplier.Name,
                });
                return;
            }
        }
        setSelectedSupplier(null);
    };

    const handleSupplierConfirmation = async () => {
        if (!project?.id) return;

        try {
            await deleteOldProjectSuppliers();
            const cartItems = await listCartByProjectIdSvc.mutateAsync({ ProjectId: project.id });

            if (purchaseStrategy === "single" && selectedSupplier) {
                await handleSingleSupplierConfirmation(cartItems);
            } else if (purchaseStrategy === "multiple" && Object.keys(itemSupplierSelections).length > 0) {
                await handleMultipleSupplierConfirmation(cartItems);
            }

            // Reload suppliers with inventory to get updated list for display
            const suppliersWithInventory = await listProjectSuppliersWithInventorySvc.mutateAsync({
                ProjectId: project.id,
            });

            if (suppliersWithInventory && suppliersWithInventory.length > 0) {
                setSavedProjectSupplierIds(suppliersWithInventory.map((ps: any) => ps.Id));
                setSavedSuppliersInfo(
                    suppliersWithInventory.map((ps: any) => ({
                        id: ps.SupplierId,
                        name: ps.SupplierName,
                    }))
                );
            } else {
                // Fallback to basic list if inventory endpoint fails
                const updatedSuppliers = await listProjectSuppliersSvc.mutateAsync({ ProjectId: project.id });
                if (updatedSuppliers && updatedSuppliers.length > 0) {
                    setSavedProjectSupplierIds(updatedSuppliers.map((ps: any) => ps.Id));
                    setSavedSuppliersInfo(
                        updatedSuppliers.map((ps: any) => ({
                            id: ps.SupplierId,
                            name: ps.SupplierName,
                        }))
                    );
                }
            }

            // Update original selections after confirmation
            if (purchaseStrategy === "single" && selectedSupplier) {
                const supplier = suppliers.find((s: ISupplier) => s.Name === selectedSupplier.name);
                if (supplier) {
                    setOriginalSelectedSupplierId(supplier.Id);
                }
            } else if (purchaseStrategy === "multiple") {
                setOriginalItemSelections({ ...itemSupplierSelections });
            }

            updateContextSupplier();
            setIsChangingSelection(false);
            setOpenSupplierComparison(false);

            // Trigger a page refresh simulation by reloading cart items
            // This ensures selectedValues get updated with supplier info and prices refresh
            window.dispatchEvent(new Event("cartItemsUpdated"));
        } catch (error) {
            console.error("Error saving supplier selection:", error);
        }
    };

    useEffect(() => {
        const isSupplierSelected =
            purchaseStrategy === "single" ? Boolean(selectedSupplier) : Object.keys(itemSupplierSelections).length > 0;
        setIsNextStageEligible({ isSupplierSelected });
    }, [selectedSupplier, purchaseStrategy, itemSupplierSelections, setIsNextStageEligible]);

    const canCompare = selectedSuppliers.length > 0 && Object.keys(selectedValues).length > 0 && !isComparing;

    return {
        // State
        openSupplierComparison,
        comparisonResults,
        purchaseStrategy,
        selectedSuppliers,
        isComparing,
        supplierSearchQuery,
        savedSuppliersInfo,
        itemSupplierSelections,
        selectedSupplier,
        activeSuppliers,
        isLoadingSuppliers,
        canCompare,
        isChangingSelection,
        // Actions
        setOpenSupplierComparison,
        setSupplierSearchQuery,
        toggleSupplierSelection,
        handleOnCompare,
        handleClearSelection,
        handleItemSupplierSelect,
        handleSupplierConfirmation,
    };
}
